import { useEffect } from 'react'
import { useAppStore } from '../store/appStore'

// This component ensures section data is always in localStorage
// for the standalone 3D viewer to read
// Only renders when 3D mode is enabled to avoid unnecessary data loads
export default function ThreeDViewerDataSync() {
  const is3DMode = useAppStore((state) => state.is3DMode)
  const currentSectionId = useAppStore((state) => state.currentSectionId)
  const cameras = useAppStore((state) => state.cameras)
  const points3D = useAppStore((state) => state.points3D)
  const shopId = useAppStore((state) => state.currentShopId)
  
  console.log('🔄 ThreeDViewerDataSync render:', {
    is3DMode,
    currentSectionId,
    camerasCount: cameras?.length,
    pointsCount: points3D?.length,
    shopId
  })
  
  // Skip if 3D mode not enabled
  if (!is3DMode) {
    console.log('⏭️  [ThreeDViewerDataSync] Skipping load: 3D mode disabled')
    return null
  }
  
  useEffect(() => {
    // Write current section data to localStorage for standalone viewer
    if (currentSectionId === null || currentSectionId === undefined || !cameras || !points3D || !shopId) return
    
    const sectionName = `section_${currentSectionId}`
    
    const camerasData = {
      cameras: cameras.map(cam => ({
        id: cam.id,
        frame_number: cam.frame_number,
        frame_file: cam.frame_file,
        position: cam.position,
        rotation: cam.rotation,
        camera_id: cam.camera_id,
        intrinsics: cam.intrinsics
      }))
    }
    
    const pointsData = {
      point_count: points3D.length,
      points: points3D.map(p => ({
        id: p.id,
        position: p.position,
        color: p.color,
        error: p.error,
        track_length: p.track_length
        // Note: cameras array stored separately in 3d-viewer-camera-to-points to save space
      }))
    }
    
    // Build camera-to-points mapping separately to avoid localStorage quota issues
    const cameraToPointsMapping = {}
    
    cameras.forEach((camera, cameraIdx) => {
      const pointIds = []
      points3D.forEach((point, pointIdx) => {
        if (point.cameras && point.cameras.some(camRef => camRef.camera_id === camera.id)) {
          pointIds.push(pointIdx)
        }
      })
      if (pointIds.length > 0) {
        cameraToPointsMapping[camera.id] = pointIds
      }
    })
    
    // Store in localStorage (persists across tabs) for the new tab to read
    // Wrap in try-catch to handle quota exceeded errors gracefully
    try {
      localStorage.setItem('3d-viewer-cameras', JSON.stringify(camerasData))
      localStorage.setItem('3d-viewer-points', JSON.stringify(pointsData))
      localStorage.setItem('3d-viewer-camera-to-points', JSON.stringify(cameraToPointsMapping))
      localStorage.setItem('3d-viewer-section', sectionName)
      localStorage.setItem('3d-viewer-shopId', shopId)
      
      console.log('✅ Stored reconstruction data (2D pixels will be loaded from JSON file)')
      
      console.log('✅ Updated 3D viewer data in localStorage:', {
        section: sectionName,
        shopId,
        cameras: cameras.length,
        points: points3D.length
      })
    } catch (error) {
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        console.warn('⚠️  [ThreeDViewerDataSync] localStorage quota exceeded - 3D data kept in memory only')
        // Data remains in component state, just not persisted to localStorage
        // This prevents the app from crashing due to storage errors
      } else {
        console.error('[ThreeDViewerDataSync] Error storing 3D data:', error)
      }
    }
  }, [currentSectionId, cameras, points3D, shopId, is3DMode])
  
  // This component doesn't render anything
  return null
}
